import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Dispatch, SetStateAction, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { PrimaryButton } from "../../components/Buttons";
import { ToggleInput } from "../../components/Inputs";
import { useLoaderStore, useUserStore } from "../../context";
import { useAlert, useApi } from "../../hooks";
import constants from "../../utils/constants";
import { classNames, formatCurrency } from "../../utils/helpers";
import React from "react";
import { ConfirmationModal } from "../../components/Modals";

const { DURATION_TYPES } = constants;

export default function Products({
  products,
  productDetails,
  currentSubscription,
  defaultPaymentMethod,
  selectedDurationType,
  setSelectedDurationType,
  eligibleOffers,
  refreshData
}) {
  const alert = useAlert();
  const { fetch, post } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { activeSystemTenant, activeAccount } = useUserStore();
  const { currentUser } = useUserStore();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [confirmationProduct, setConfirmationProduct] = useState();
  const [confirmProductOptions, setConfirmProductOptions] = useState({
    isCurrentSubscription: false,
    showResetRenewSubscription: false,
    canUpgrade: false
  });
  // SPR-011 T-004: the user's chosen offer per product (productId -> trialOfferId). Absent => the best-value
  // default (first element; the server returns each list best-value first). Lets the user pick among the full list.
  const [selectedOfferIds, setSelectedOfferIds] = useState({});

  // SPR-008 T-002b: eligible behavioral offers for a product. The eligibleOffers map is keyed by productId
  // (string keys via JSON); JS coerces the numeric productId for the lookup.
  const offersForProduct = (productId) => {
    const list = eligibleOffers && eligibleOffers[productId];
    return Array.isArray(list) ? list : [];
  };

  // SPR-011 T-004: the offer to apply for a product = the user's chosen one, else the best-value default (first).
  const offerForProduct = (productId) => {
    const list = offersForProduct(productId);
    if (list.length === 0) return undefined;
    const chosenId = selectedOfferIds[productId];
    return list.find((o) => o.trialOfferId === chosenId) ?? list[0];
  };

  // Offer display text mirrors CancelConfirmationModal so the plan grid and cancel flow read consistently.
  const offerDiscountText = (offer) => {
    if (!offer) return "";
    if (offer.discountPercent != null) return `${offer.discountPercent}% off`;
    if (offer.discountType === "Fixed") return `${formatCurrency(offer.price, true)} for the offer term`;
    return offer.discountType || "Discount applies";
  };

  // Human-readable duration unit (e.g. "Months" -> "months", singularized when count === 1).
  const unitLabel = (durationType, count) => {
    if (!durationType) return "";
    let u = durationType.toLowerCase();
    if (count === 1 && u.endsWith("s")) u = u.slice(0, -1);
    return u;
  };

  // SPR-011 T-004: recurring-aware term copy ("6 months (recurring)" vs "3-month term").
  const offerTermText = (offer) => {
    if (!offer) return "";
    if (offer.discountMode === "RecurringPerPeriod") {
      const n = offer.recurringPeriods ?? 1;
      return `${n} ${unitLabel(offer.durationType, n)} (recurring)`;
    }
    return `${offer.durationCount} ${offer.durationType}`;
  };

  // SPR-011 T-004: one-line offer summary for the per-product chooser.
  const describeOffer = (offer) => {
    if (!offer) return "";
    const discount = offer.discountPercent != null
      ? `${offer.discountPercent}% off`
      : (offer.discountType === "Fixed" ? formatCurrency(offer.price, true) : (offer.discountType || "Discount"));
    if (offer.discountMode === "RecurringPerPeriod") {
      const n = offer.recurringPeriods ?? 1;
      return `${discount} for ${n} ${unitLabel(offer.durationType, n)}`;
    }
    return `${discount} for a ${offer.durationCount}-${unitLabel(offer.durationType, 1)} term`;
  };

  // Indicative discounted price for display only (in pennies). The server re-computes and applies the
  // authoritative price at purchase from the trialOfferId we pass — this never drives the charge.
  const offerDiscountedPrice = (offer, productPriceInPennies) => {
    if (!offer) return undefined;
    if (offer.discountPercent != null)
      return Math.round(productPriceInPennies * (1 - offer.discountPercent / 100));
    if (offer.discountType === "Fixed") return offer.price;
    return undefined;
  };

  const purchaseSubscription = (productId, productPrice, trialOfferId) => {
    if (currentSubscription || !isChangeSubscriptionValid(productPrice)) return;
    setShowLoader(true);
    let payload = {
      ProductId: productId,
      Qty: 1,
      Autorenew: true,
      NotifyTenantUsers: true,
    };
    // SPR-008 T-002b: apply the eligible offer through the EXISTING purchase path (client passes the offer id;
    // the server re-validates the same gate and applies the pricing). Upgrade/switch do not support offers.
    if (trialOfferId != null) payload.TrialOfferId = trialOfferId;
    post(
      `admin/PurchaseMargotProduct/${activeAccount.id}/${activeSystemTenant?.id}`, payload
    )
      .then(() => {
        alert(
          "Successfully purchased subscription",
          "Your subscription has been purchased",
          "success"
        );
        refreshData();
      })
      .catch((err) => {
        console.error(err);
        alert(
          "Could not purchase subscription",
          err?.data?.message || "Unable to complete transaction",
          "error"
        );
      })
      .finally(() => {
        setShowLoader(false);
      });
  }

  const upgradeSubscription = (upgradeProductId, productPrice, trialOfferId) => {
    if (!currentSubscription || !isChangeSubscriptionValid(productPrice)) return;

    setShowLoader(true);
    let payload = {
      OwnedProductId: currentSubscription.ownedProductId,
      UpgradeProductId: upgradeProductId,
      PaymentMethodId: defaultPaymentMethod.id,
      Qty: 1,
      Autorenew: true,
      NotifyTenantUsers: true,
      // StripeCustomerId: stripeCustomerId, // GETTING FROM SERVER
      // TenantId: tenantId, // GETTING FROM SERVER
    };
    // SPR-008 T-002 (Option B): upgrade applies the offer immediately (server re-validates + prices).
    if (trialOfferId != null) payload.TrialOfferId = trialOfferId;
    post(`admin/UpgradeSystemAccountSubscriptionNow/${activeAccount.id}`, payload).then(() => {
        alert(
          "Successfully upgraded subscription",
          "Your subscription has been upgraded",
          "success"
        );
        refreshData();
      })
      .catch((err) => {
        console.error(err);
        alert(
          "Could not upgrade subscription",
          err?.data?.message || "Unable to complete transaction",
          "error"
        );
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const switchSubscription = (renewalProductId, productPrice, trialOfferId) => {
    if (!currentSubscription || !isChangeSubscriptionValid(productPrice)) return;

    setShowLoader(true);
    // SPR-008 T-002 (Option B): a switch applies the offer at the NEXT RENEWAL (server re-validates + stamps it).
    const url = trialOfferId != null
      ? `admin/ChangeSystemAccountSubscription/${currentSubscription.ownedProductId}/${renewalProductId}/${trialOfferId}`
      : `admin/ChangeSystemAccountSubscription/${currentSubscription.ownedProductId}/${renewalProductId}`;
    post(url)
      .then(() => {
        alert(
          "Successfully switched subscription",
          "Your subscription has been changed",
          "success"
        );
        refreshData();
      })
      .catch((err) => {
        alert(
          "Could not switch subscription",
          err?.data?.message || "Unable to complete transaction",
          "error"
        );
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const isChangeSubscriptionValid = (productPrice) => {
    if (defaultPaymentMethod === undefined && productPrice !== 0) {
      alert(
        "No default payment method",
        "Create a payment method and set it as your default",
        "warning"
      );
      return false;
    }
    return true;
  };

  const resolveButtonText = (isCurrentSubscription, canUpgrade, showResetRenewSubscription) => {
    if (isCurrentSubscription && showResetRenewSubscription) return "Set as renewal plan";
    if (isCurrentSubscription) return "Your current plan";
    if (!currentSubscription) return "Purchase";
    return canUpgrade
      ? "Upgrade"
      : "Switch"
  }

  const resolveConfirmationText = (isCurrentSubscription, canUpgrade, showResetRenewSubscription, productName) => {
    let phrase = '';
    if (isCurrentSubscription && showResetRenewSubscription) phrase = `set ${productName} as renewal plan`;
    if (!currentSubscription) phrase = `purchase ${productName}`;
    else{
      phrase = canUpgrade
        ? `upgrade to ${productName}`
        : `switch to ${productName}`;
    }  
    return `Are you sure you want to ${phrase}?`;
  }

  const resolveSystemTenantLimit = (details) => {
    if (typeof details.numberOfSystemTenants === "number"){
      if (details.numberOfSystemTenants === 1) return `${details.numberOfSystemTenants} System Tenant`
      return `Up to ${details.numberOfSystemTenants} System Tenants`;
    }
    return "Unlimited System Tenants";
  }

  const resolveSubText = (productPriceInPennies, canUpgrade, isCurrentSubscription, showResetRenewSubscription) => {
    if ( isCurrentSubscription && showResetRenewSubscription ) 
      return "Your CurrentPlan";
    else if ( isCurrentSubscription && !showResetRenewSubscription ) 
      return null;
    else if (canUpgrade || productPriceInPennies === currentSubscription?.priceInPennies) 
      return "You will be immediately placed onto this plan";
    else 
      return "You will be placed onto this plan at the end of your current subscription period";
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex justify-center mb-12 gap-x-2">
        <ToggleInput
          enabled={selectedDurationType === "Years"}
          setEnabled={() =>
            setSelectedDurationType((x) => (x === "Years" ? "Months" : "Years"))
          }
        />
        {selectedDurationType === "Years" ? "Annual" : "Monthly"}
      </div>
      <div className="grid grid-cols-1 gap-y-16 divide-y divide-gray-200 sm:mx-auto lg:-mx-8 lg:mt-0 lg:max-w-none lg:grid-cols-3 lg:divide-x lg:divide-y-0 xl:-mx-4">
        {products
          .filter(
            (x) =>
              x.durationType === selectedDurationType//DURATION_TYPES.find(d => d.label === selectedDurationType)?.value //DURATION_TYPES.BY_NAME[selectedDurationType]
          )
          .sort((a, b) => a.price - b.price)
          .map((x, i) => {
            const details = productDetails && productDetails.length && productDetails.find(p => p.productId === x.productId);
            const canUpgrade = !!(currentSubscription?.price !== undefined && x.price > currentSubscription.price);
            const isCurrentSubscription = !!(currentSubscription && currentSubscription?.productId === x.productId);
            const showResetRenewSubscription = !!(isCurrentSubscription && currentSubscription?.productId !== currentSubscription?.renewalProductId);
            // SPR-008 T-002b / SPR-011 T-004: eligible behavioral offers for this plan (if any) and the
            // currently-applied one (user's choice, else the best-value default = first).
            const offers = offersForProduct(x.productId);
            const offer = offerForProduct(x.productId);
            const discountedPrice = offerDiscountedPrice(offer, x.price);
            // SPR-008 T-002 (Option B): purchase & upgrade apply the offer immediately (show the discounted price);
            // a switch applies it at the NEXT RENEWAL, so we keep the current price and label it instead of showing
            // a misleading immediate discount.
            const isImmediateOffer = !!offer && (!currentSubscription || canUpgrade);
            return (
              <div
                key={x.productId}
                className="pt-16 lg:px-8 lg:pt-0 xl:px-14 flex flex-col gap-4"
              >
                <h3 className="text-base font-semibold leading-7 text-gray-900">
                  {x.name}
                </h3>
                {offer && (
                  <span className="inline-flex items-center self-start rounded-full bg-primaryColor/10 px-3 py-1 text-xs font-semibold text-primaryColor">
                    {`Offer: ${offer.name}`}
                  </span>
                )}
                {x.description &&
                (<p className="text-sm leading-6 text-gray-500">
                  {x.description}
                </p>)}
                <p className="flex items-baseline gap-x-1">
                  {isImmediateOffer && typeof discountedPrice === "number" && x.price > 0 ? (
                    <>
                      <span className="text-5xl font-bold tracking-tight text-gray-900">
                        {formatCurrency(discountedPrice, true)}
                      </span>
                      <span className="text-lg font-medium text-gray-400 line-through">
                        {formatCurrency(x.price, true)}
                      </span>
                      <span className="text-sm font-semibold leading-6 text-gray-600">
                        /{selectedDurationType === "Years" ? "year" : "month"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-5xl font-bold tracking-tight text-gray-900">
                        {x.price === 0
                          ? "Free"
                          : formatCurrency(x.price, true)}
                      </span>
                      {x.price > 0 && (
                        <span className="text-sm font-semibold leading-6 text-gray-600">
                          /{selectedDurationType === "Years" ? "year" : "month"}
                        </span>
                      )}
                    </>
                  )}
                </p>
                {offer && (
                  <div className="flex flex-col gap-1 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Discount: </span>
                      {offerDiscountText(offer)}
                    </div>
                    <div>
                      <span className="font-medium">Term: </span>
                      {offerTermText(offer)}
                    </div>
                    {!isImmediateOffer && (
                      <div className="italic text-primaryColor">
                        Offer applies at your next renewal
                      </div>
                    )}
                  </div>
                )}
                {/* SPR-011 T-004: when more than one offer is eligible, let the user pick; the first is the
                    best-value default (server-ordered). The chosen offer id is threaded through purchase/upgrade/switch. */}
                {offers.length > 1 && (
                  <div className="flex flex-col gap-2 text-sm text-gray-700">
                    <div className="font-medium">Choose your offer:</div>
                    {offers.map((o, oi) => {
                      const isSelected = o.trialOfferId === offer.trialOfferId;
                      return (
                        <label
                          key={o.trialOfferId}
                          className={classNames(
                            "p-2 rounded-md border cursor-pointer flex flex-row items-start gap-2",
                            isSelected ? "border-primaryColor" : "border-gray-200 hover:border-primaryColor/40"
                          )}
                        >
                          <input
                            type="radio"
                            name={`offer-${x.productId}`}
                            className="mt-1"
                            checked={isSelected}
                            onChange={() =>
                              setSelectedOfferIds((prev) => ({ ...prev, [x.productId]: o.trialOfferId }))
                            }
                          />
                          <span className="flex flex-col">
                            <span className="flex flex-row items-center gap-2">
                              <span className="font-medium">{o.name}</span>
                              {oi === 0 && (
                                <span className="inline-flex items-center rounded-full bg-primaryColor/10 px-2 py-0.5 text-xs font-semibold text-primaryColor">
                                  Best value
                                </span>
                              )}
                            </span>
                            <span className="text-gray-600">{describeOffer(o)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <PrimaryButton
                  text={resolveButtonText(isCurrentSubscription, canUpgrade, showResetRenewSubscription)}
                  disabled={isCurrentSubscription && !showResetRenewSubscription}
                  onClick={() => {
                    setConfirmationProduct(x);
                    setConfirmProductOptions({
                      isCurrentSubscription,
                      showResetRenewSubscription,
                      canUpgrade
                    });
                    setPurchaseModalOpen(true);
                  }}
                />

                <div
                  className={classNames(
                    "flex justify-end text-md text-gray-400",
                    isCurrentSubscription && !showResetRenewSubscription ? "invisible" : ""
                  )}
                >
                  <div className="w-3/4 text-right">
                    {resolveSubText(x.price, canUpgrade, isCurrentSubscription, showResetRenewSubscription)}
                  </div>
                </div>
                {details && (
                  <ul
                    className="mt-auto list-none space-y-3 text-md leading-6 text-gray-600"
                  >
                    <li className="flex gap-x-3">
                      <CheckCircleIcon className="h-6 w-5 flex-none text-primaryColor" />
                      {resolveSystemTenantLimit(details)}
                    </li>
                    <li className="flex gap-x-3">
                      <CheckCircleIcon className="h-6 w-5 flex-none text-primaryColor" />
                      {`${details.transactionFeeAmount}% Transaction fee `}
                    </li>
                  </ul>
                )}
              </div>
            );
          })}
      </div>
      <ConfirmationModal
        modalOpen={purchaseModalOpen}
        setModalOpen={setPurchaseModalOpen}
        showIcon={false}
        title={resolveConfirmationText(confirmProductOptions.isCurrentSubscription, confirmProductOptions.canUpgrade, confirmProductOptions.showResetRenewSubscription, confirmationProduct?.name)}
        message={"Enter username to confirm."}
        confirmButtonText={"Confirm"}
        textToMatch={currentUser?.username}
        onConfirm={() => {
          if ((confirmProductOptions.isCurrentSubscription && !confirmProductOptions.showResetRenewSubscription) || confirmationProduct?.productId === undefined)
            return;
          // SPR-008 T-002b: on a fresh purchase, apply the eligible offer via the purchase path (trialOfferId).
          // SPR-008 T-002 (Option B): all three paths honor the eligible offer (server re-validates the id in every
          // case). Purchase & upgrade apply it immediately; switch applies it at the next renewal.
          else if (!currentSubscription) purchaseSubscription(confirmationProduct?.productId, confirmationProduct?.price, offerForProduct(confirmationProduct?.productId)?.trialOfferId);
          else if (confirmProductOptions.canUpgrade) upgradeSubscription(confirmationProduct?.productId, confirmationProduct?.price, offerForProduct(confirmationProduct?.productId)?.trialOfferId);
          else switchSubscription(confirmationProduct?.productId, confirmationProduct?.price, offerForProduct(confirmationProduct?.productId)?.trialOfferId);
          setPurchaseModalOpen(false);
        }}
        on
      />
    </div>
  );
}
